// B-71 — "Enviar consolidado": the pure half of sending a day's report from its detail.
//
// The backend sends the day's summary, everything still open and the Matriz (`sendCierreDailyReport`)
// to the addresses typed here, or to the client's configured recipients when none were typed. It
// answers as it goes — one JSON line per step (`leerEventos`) — so the dialog shows what is being
// done (`estadoDePasos`) instead of a spinner. What it can refuse is said here in the operator's
// words, because its own sentences are written for a developer reading a log.

import { correctionError } from "./cierreCorrections";

const MENSAJES = {
  NO_RECIPIENTS: "Escribe al menos un correo: este cliente no tiene destinatarios configurados.",
  SEND_FAILED: "No se pudo enviar el correo. Inténtalo de nuevo en unos minutos.",
  INVALID_ARGUMENT: "Revisa los correos: alguno no es una dirección válida.",
};

// The backend's own rule (`notifications/settings.js`): one @, something on each side, a dot in
// the domain, no spaces.
const CORREO = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// What was typed in "Para": addresses separated by commas, semicolons, spaces or new lines —
// trimmed, lowercased, without repeats — and those that are not addresses.
export function correosEscritos(texto) {
  const partes = String(texto || "")
    .split(/[\s,;]+/)
    .map((p) => p.trim().toLowerCase())
    .filter(Boolean);
  const unicos = [...new Set(partes)];
  return {
    correos: unicos.filter((p) => CORREO.test(p)),
    invalidos: unicos.filter((p) => !CORREO.test(p)),
  };
}

// What the dialog says once the backend answered: `{ ok, texto }`.
export function resultadoDeEnvio(response) {
  if (response?.success) {
    const { recipients = 0, matrix = null } = response.data || {};
    const quienes = recipients === 1 ? "1 destinatario" : `${recipients} destinatarios`;
    return {
      ok: true,
      texto: matrix ? `Enviado a ${quienes} con la Matriz adjunta (${matrix}).` : `Enviado a ${quienes}.`,
    };
  }
  return { ok: false, texto: MENSAJES[response?.errorCode] || correctionError(response) };
}

// --- Progress ---------------------------------------------------------------------------------

const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;

// The steps the backend reports, in order, with what each says when it has something to say.
const PASOS = [
  {
    paso: "closings",
    titulo: "Leer los cierres del día",
    detalle: (d, estado) => (estado === "done" && d?.offices != null ? plural(d.offices, "oficina", "oficinas") : null),
  },
  {
    paso: "offices",
    titulo: "Revisar alertas y documentos",
    detalle: (d, estado) => {
      if (estado === "done" && d) {
        return `${plural(d.withAlerts || 0, "oficina con alerta", "oficinas con alerta")} · ${plural(
          d.withMissing || 0,
          "con documentos faltantes",
          "con documentos faltantes"
        )}`;
      }
      return d?.offices != null ? plural(d.offices, "oficina", "oficinas") : null;
    },
  },
  {
    paso: "matrix",
    titulo: "Generar la Matriz de Conciliación",
    detalle: (d, estado) => {
      if (estado === "failed") return "No se pudo generar: el correo sale sin el adjunto.";
      return estado === "done" ? d?.file || null : null;
    },
  },
  {
    paso: "send",
    titulo: "Enviar el correo",
    detalle: (d) => (d?.recipients != null ? `a ${plural(d.recipients, "destinatario", "destinatarios")}` : null),
  },
];

// The checklist the dialog draws, from the progress events received so far: each step
// `pending`, `running`, `done` or `failed`, with its detail. The latest event of a step wins.
export function estadoDePasos(eventos) {
  return PASOS.map(({ paso, titulo, detalle }) => {
    const ultimo = [...(eventos || [])].reverse().find((e) => e?.step === paso);
    const estado = ultimo?.state || "pending";
    return { paso, titulo, estado, detalle: ultimo ? detalle(ultimo.detail, estado) : null };
  });
}

// Read an NDJSON answer as it arrives: every `progress` line to `onProgreso`, and the `result` line
// returned (without its `type`), or null if the stream ended without one. `reader` is a
// `ReadableStream` reader of bytes.
export async function leerEventos(reader, onProgreso) {
  const decoder = new TextDecoder();
  let pendiente = "";
  let resultado = null;
  const linea = (texto) => {
    const limpio = texto.trim();
    if (!limpio) return;
    let evento;
    try {
      evento = JSON.parse(limpio);
    } catch {
      return;
    }
    if (evento.type === "progress") {
      if (onProgreso) onProgreso(evento);
    } else if (evento.type === "result") {
      const { type, ...resto } = evento;
      resultado = resto;
    }
  };
  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    const { value, done } = await reader.read();
    if (done) break;
    pendiente += decoder.decode(value, { stream: true });
    let corte = pendiente.indexOf("\n");
    while (corte >= 0) {
      linea(pendiente.slice(0, corte));
      pendiente = pendiente.slice(corte + 1);
      corte = pendiente.indexOf("\n");
    }
  }
  linea(pendiente + decoder.decode());
  return resultado;
}
